import * as React from 'react';

import Image from 'lib/image-container';
import { useDetectPrint } from 'lib/use-detect-print';
import { MediaQueryWidth } from 'lib/use-media-query';
import useScrollbarWidth from 'lib/use-scrollbar-width';

import { Columns } from 'components/columns';
import ColumnsItem, { ColumnsSize } from 'components/columns/columns-item';
import { CtaBox, CtaBoxHeader, CtaBoxText } from 'components/cta-box';
import GoodToKnow from 'components/good-to-know/good-to-know';
import PageSection from 'components/page/page-section';
import {
  COLUMNS_GAP_MEDIUM,
  IMAGE_MAX_WIDTH_1,
  PAGE_PADDING_LARGE,
  PAGE_PADDING_MEDIUM,
  PAGE_PADDING_SMALL,
} from 'components/page/sections';
import PageSectionCta from 'components/page/sections/page-section-cta';
import PageSectionCtaWithoutImage from 'components/page/sections/page-section-cta-without-image';
import SliderBox from 'components/slider-box/slider-box';
import { Header3, P1 } from 'components/typography';

import { getPropsSectionOneContainer } from './section-one-container.props';

export const SectionOneContainer: React.FC = () => {
  const props = getPropsSectionOneContainer();
  const scrollbarWidth = useScrollbarWidth(true);
  const imageSizes = `
        (max-width: ${MediaQueryWidth['desktop-sm']}px) calc(100vw - 2 * ${PAGE_PADDING_SMALL} - ${scrollbarWidth}),
        (max-width: ${MediaQueryWidth['tablet-lg']}px) calc((100vw - 2 * ${PAGE_PADDING_MEDIUM} - ${COLUMNS_GAP_MEDIUM} - ${scrollbarWidth}) / 2),
        (max-width: ${MediaQueryWidth['content-max']}px) calc((100vw - 2 * ${PAGE_PADDING_LARGE} - ${COLUMNS_GAP_MEDIUM} - ${scrollbarWidth}) / 2),
        ${IMAGE_MAX_WIDTH_1}
    `;
  const isPrinting = useDetectPrint();
  return (
    <>
      <PageSection extraClass="section-cta" padding="block" inner={true}>
        <Header3>
          <b>{props.sections.one.intro}</b>
        </Header3>
      </PageSection>
      <PageSectionCta
        titleElement={props.sections.one.title}
        text={props.sections.one.text}
        image={
          isPrinting
            ? undefined
            : {
                src: props.sections.one.imageSrc,
                objectPosition: props.sections.one.imageObjectPosition,
                objectFit: 'contain',
              }
        }
        columnLeft={
          isPrinting
            ? undefined
            : {
                size: ColumnsSize['1/3'],
                content: (
                  <div className="speech-box speech-box--relatively-full-width speech-box--primary speech-box--over-next-bottom">
                    {props.sections.one.imageText}
                  </div>
                ),
              }
        }
        columnRight={
          isPrinting
            ? {
                size: ColumnsSize['2/3'],
                content: (
                  <div className="speech-box speech-box--relatively-full-width speech-box--primary speech-box--over-next-bottom">
                    {props.sections.one.imageText}
                  </div>
                ),
              }
            : { size: ColumnsSize['2/3'] }
        }
        color="transparent"
      />
      <PageSection color="peach" extraClass="section-cta" padding="block" inner={true}>
        <div className="page__section-inner-item">
          <Columns hasMobileLayout={true} vAlignTop={true}>
            <ColumnsItem verticalAlign="stretch" size={ColumnsSize['1/2']}>
              {isPrinting ? null : (
                <div className="page-section__picture">
                  <Image
                    width={600}
                    height={620}
                    style={{
                      objectFit: 'contain',
                      objectPosition: props.sections.two.imageObjectPosition,
                    }}
                    quality={75}
                    src={props.sections.two.imageSrc}
                    alt={props.sections.two.imageAlt}
                    sizes={imageSizes}
                  />
                </div>
              )}
            </ColumnsItem>

            <ColumnsItem size={ColumnsSize['1/2']}>
              <CtaBox align="start">
                <CtaBoxHeader tag="h2">{props.sections.two.title}</CtaBoxHeader>
                <CtaBoxText>
                  <P1 as="p">{props.sections.two.text}</P1>
                </CtaBoxText>
              </CtaBox>
            </ColumnsItem>
          </Columns>
        </div>
        <div className="page__section-inner-item">
          <CtaBox align="start">
            <CtaBoxHeader tag="h3">{props.sections.two.subSection1.title}</CtaBoxHeader>
          </CtaBox>
        </div>
        <div className="page__section-inner-item">
          <Columns hasMobileLayout={true} vAlignTop={true}>
            <ColumnsItem verticalAlign="stretch" size={ColumnsSize['1/2']}>
              <CtaBox align="start">
                <CtaBoxText>{props.sections.two.subSection1.facebook.text}</CtaBoxText>
              </CtaBox>
            </ColumnsItem>

            <ColumnsItem size={ColumnsSize['1/2']}>
              <CtaBox align="start">
                <CtaBoxText>{props.sections.two.subSection1.apple.text}</CtaBoxText>

                <br />
                <div className="speech-box speech-box--soft-white">
                  <P1 as="p">{props.sections.two.subSection1.apple.speechBox}</P1>
                </div>
              </CtaBox>
            </ColumnsItem>
          </Columns>
        </div>
        <div className="page__section-inner-item">
          <P1 as="p">{props.sections.two.subSection1.lastText}</P1>
        </div>
        <div className="page__section-inner-item">
          <Columns hasMobileLayout={true} vAlignTop={true}>
            <ColumnsItem size={ColumnsSize['1/2']}>
              <CtaBox align="start">
                <CtaBoxHeader tag="h3">{props.sections.two.subSection2.title}</CtaBoxHeader>

                <CtaBoxText>{props.sections.two.subSection2.text}</CtaBoxText>
              </CtaBox>
            </ColumnsItem>
            <ColumnsItem verticalAlign="stretch" size={ColumnsSize['1/2']}>
              <GoodToKnow
                title={props.sections.two.subSection2.goodToKnow.title}
                content={props.sections.two.subSection2.goodToKnow.content}
              />
            </ColumnsItem>
          </Columns>
        </div>
      </PageSection>
      <PageSection extraClass="section-cta" padding="block" inner={true}>
        <Columns hasMobileLayout={true}>
          <ColumnsItem size={isPrinting ? ColumnsSize['1/1'] : ColumnsSize['2/3']}>
            <CtaBox align="start">
              <CtaBoxHeader tag="h2">{props.sections.three.title}</CtaBoxHeader>
              <CtaBoxText>{props.sections.three.text}</CtaBoxText>
            </CtaBox>
          </ColumnsItem>
          {isPrinting ? null : (
            <ColumnsItem verticalAlign="stretch" size={ColumnsSize['1/3']}>
              <Image
                width={375}
                height={812}
                style={{
                  objectFit: 'contain',
                  objectPosition: props.sections.three.imageObjectPosition,
                }}
                quality={75}
                src={props.sections.three.imageSrc}
                alt={props.sections.three.imageAlt}
                sizes={imageSizes}
              />
            </ColumnsItem>
          )}
        </Columns>
      </PageSection>
      <PageSection color="peach" extraClass="section-cta" padding="block" inner={true}>
        <Columns hasMobileLayout={true} vAlignTop={true}>
          <ColumnsItem verticalAlign="stretch" size={ColumnsSize['1/3']}>
            <GoodToKnow
              title={props.sections.four.goodToKnow.title}
              content={props.sections.four.goodToKnow.content}
            />
          </ColumnsItem>

          <ColumnsItem size={ColumnsSize['2/3']}>
            <CtaBox align="start">
              <CtaBoxHeader tag="h2">{props.sections.four.title}</CtaBoxHeader>
              <CtaBoxText>{props.sections.four.text}</CtaBoxText>
            </CtaBox>
          </ColumnsItem>
        </Columns>
      </PageSection>
      <PageSectionCtaWithoutImage
        titleElement={props.sections.five.title}
        text={props.sections.five.text}
        imageText={
          <GoodToKnow
            title={props.sections.five.goodToKnow.title}
            contentHtml={props.sections.five.goodToKnow.text}
          />
        }
        columnLeft={{ size: ColumnsSize['1/3'] }}
        columnRight={{ size: ColumnsSize['2/3'] }}
        color="transparent"
      />
      <PageSection color="peach" extraClass="section-cta" padding="block" inner={true}>
        <Columns hasMobileLayout={true} vAlignTop={true}>
          {isPrinting ? null : (
            <ColumnsItem verticalAlign="stretch" size={ColumnsSize['1/2']}>
              <div className="page-section__picture">
                <Image
                  width={600}
                  height={620}
                  style={{
                    objectFit: 'contain',
                    objectPosition: props.sections.six.imageObjectPosition,
                  }}
                  quality={75}
                  src={props.sections.six.imageSrc}
                  alt=""
                  sizes={imageSizes}
                />
              </div>
            </ColumnsItem>
          )}

          <ColumnsItem size={isPrinting ? ColumnsSize['1/1'] : ColumnsSize['1/2']}>
            <CtaBox align="start">
              <CtaBoxHeader tag="h2">{props.sections.six.title}</CtaBoxHeader>

              <CtaBoxText>{props.sections.six.text}</CtaBoxText>
            </CtaBox>
          </ColumnsItem>
        </Columns>
      </PageSection>
      <PageSection padding="block" inner={true}>
        <Columns hasMobileLayout={true} vAlignTop={true}>
          <ColumnsItem size={isPrinting ? ColumnsSize['1/1'] : ColumnsSize['1/2']}>
            <CtaBox align="start">
              <CtaBoxHeader tag="h2">{props.sections.seven.title}</CtaBoxHeader>
              <CtaBoxText>{props.sections.seven.text}</CtaBoxText>
            </CtaBox>
          </ColumnsItem>
          {isPrinting ? null : (
            <ColumnsItem verticalAlign="stretch" size={ColumnsSize['1/2']}>
              <Image
                width={650}
                height={382}
                style={{
                  objectFit: 'contain',
                  objectPosition: props.sections.seven.imageObjectPosition,
                }}
                quality={75}
                src={props.sections.seven.imageSrc}
                alt={props.sections.seven.imageAlt}
                sizes={imageSizes}
              />
            </ColumnsItem>
          )}
        </Columns>
      </PageSection>
      <PageSection color="peach" padding="block" inner={true}>
        <Columns hasMobileLayout={true} vAlignTop={true}>
          {isPrinting ? null : (
            <ColumnsItem verticalAlign="stretch" size={ColumnsSize['1/2']}>
              <Image
                width={650}
                height={382}
                style={{
                  objectFit: 'contain',
                  objectPosition: props.sections.eight.imageObjectPosition,
                }}
                quality={75}
                src={props.sections.eight.imageSrc}
                alt={props.sections.eight.imageAlt}
                sizes={imageSizes}
              />
            </ColumnsItem>
          )}
          <ColumnsItem size={isPrinting ? ColumnsSize['1/1'] : ColumnsSize['1/2']}>
            <CtaBox align="start">
              <CtaBoxHeader tag="h2">{props.sections.eight.title}</CtaBoxHeader>
              <CtaBoxText>{props.sections.eight.text}</CtaBoxText>
            </CtaBox>
          </ColumnsItem>
        </Columns>
      </PageSection>
      <PageSection padding="block" inner={true}>
        <Columns hasMobileLayout={true} vAlignTop={true}>
          <ColumnsItem size={isPrinting ? ColumnsSize['1/1'] : ColumnsSize['1/2']}>
            <CtaBox align="start">
              <CtaBoxHeader tag="h2">{props.sections.twelve.title}</CtaBoxHeader>
              <CtaBoxText>{props.sections.twelve.text}</CtaBoxText>
            </CtaBox>
          </ColumnsItem>
          {isPrinting ? null : (
            <ColumnsItem verticalAlign="stretch" size={ColumnsSize['1/2']}></ColumnsItem>
          )}
        </Columns>
      </PageSection>
      <PageSection color="peach" padding="block" inner={true}>
        <Columns hasMobileLayout={true} vAlignTop={true}>
          <ColumnsItem size={isPrinting ? ColumnsSize['1/1'] : ColumnsSize['1/2']}>
            <CtaBox align="start">
              <CtaBoxHeader tag="h2">{props.sections.nine.title}</CtaBoxHeader>
              <CtaBoxText>{props.sections.nine.text}</CtaBoxText>
            </CtaBox>
          </ColumnsItem>
          {isPrinting ? null : (
            <ColumnsItem verticalAlign="stretch" size={ColumnsSize['1/2']}>
              <Image
                width={650}
                height={382}
                style={{
                  objectFit: 'contain',
                  objectPosition: props.sections.nine.imageObjectPosition,
                }}
                quality={75}
                src={props.sections.nine.imageSrc}
                alt={props.sections.nine.imageAlt}
                sizes={imageSizes}
              />
            </ColumnsItem>
          )}
        </Columns>
      </PageSection>
      <PageSection padding="block" inner={true}>
        <Columns hasMobileLayout={true} vAlignTop={true}>
          {isPrinting ? null : (
            <ColumnsItem verticalAlign="stretch" size={ColumnsSize['1/2']}>
              <Image
                width={650}
                height={382}
                style={{
                  objectFit: 'contain',
                  objectPosition: props.sections.ten.imageObjectPosition,
                }}
                quality={75}
                src={props.sections.ten.imageSrc}
                alt={props.sections.ten.imageAlt}
                sizes={imageSizes}
              />
            </ColumnsItem>
          )}
          <ColumnsItem size={isPrinting ? ColumnsSize['1/1'] : ColumnsSize['2/3']}>
            <CtaBox align="start">
              <CtaBoxHeader tag="h2">{props.sections.ten.title}</CtaBoxHeader>
              <CtaBoxText>{props.sections.ten.text}</CtaBoxText>
            </CtaBox>
          </ColumnsItem>
        </Columns>
      </PageSection>
      <PageSection color="peach" extraClass="section-cta" padding="block" inner={true}>
        <Columns hasMobileLayout={true} vAlignTop={true}>
          <ColumnsItem verticalAlign="stretch" size={ColumnsSize['1/2']}>
            <CtaBox align="start">
              <CtaBoxHeader tag="h2">{props.sections.eleven.title}</CtaBoxHeader>

              <CtaBoxText>{props.sections.eleven.text}</CtaBoxText>
            </CtaBox>
          </ColumnsItem>
          <ColumnsItem size={ColumnsSize['1/3']}>
            <SliderBox
              title={props.sections.eleven.whatIsACookie.title}
              content={props.sections.eleven.whatIsACookie.content}
              icon="cookie"
              color="orange"
            />
          </ColumnsItem>
        </Columns>
      </PageSection>
    </>
  );
};
